import React, { useState } from 'react';
import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import Image from 'gatsby-plugin-sanity-image';
import { motion, AnimatePresence } from 'framer-motion';

import { useSiteConfig } from '@querys/useSiteConfig';
import { PortableTextBlock } from '@modules/portableTextBlock';

import Facebook from '@svg/facebook.svg';
import Instagram from '@svg/instagram.svg';
import Twitter from '@svg/twitter.svg';
import Youtube from '@svg/youtube.svg';

export const Footer = ({ settings }) => {
  const {
    footerCopy,
    footerImages,
    address1,
    address2,
    email,
    phone,
    footerBannerText,
    socials,
  } = useSiteConfig();

  return (
    <div className='bg-red'>
      {/* Banner */}
      <AnimatePresence exitBeforeEnter>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`w-full text-black py-16 lg:py-28 text-center grid grid-cols-14 bg-red`}>
          <div className='col-start-3 col-end-13 lg:col-start-4 lg:col-end-12 text-black text-2xl leading-2xl md:text-5xl md:leading-5xl'>
            <h2 className='font-semibold'>{footerBannerText.heading}</h2>
            <h3>{footerBannerText.text}</h3>
          </div>

          <div className='col-start-2 col-end-14 mt-6 md:mt-12'>
            <a
              href='https://nz.patronbase.com/_DunedinSymphonyOrchestra/Signup/Form?layout=normal'
              target='_blank'
              rel='noreferrer'
              className={`text-sm lg:text-lg py-3 lg:py-4 px-8 sm:w-max lg:px-12 text-red bg-black rounded-full font-semibold`}>
              Sign up
            </a>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Desktop Footer */}
      <div className='hidden bg-black pt-24 pb-12 lg:grid grid-cols-14 text-white'>
        {/* Top row */}
        <div className='col-start-2 col-end-6'>
          <Link to='/'>
            <StaticImage
              width={345}
              height={126}
              src={'../../images/logo-footer.png'}
              alt='DSO Logo'
            />
          </Link>

          <div className='mt-12 text-xs leading-xs w-72'>
            {footerCopy.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>

        <div className='col-start-8 col-end-10 text-base leading-base'>
          <h6 className='font-semibold'>Address</h6>
          <div className='mt-3'>
            {address1.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
          <div className='mt-3'>
            {address2.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>

        <div className='col-start-11 col-end-14'>
          <div>
            <h6 className='font-semibold text-base leading-base hover:opacity-50 transition duration-300 ease-in-out'>
              <Link to='/contact'>Contact us</Link>
            </h6>
            <p className='text-3xl leading-3xl mt-4 hover:opacity-50 transition duration-300 ease-in-out'>
              <a href={`tel:${phone}`}>{phone}</a>
            </p>
            <p className='text-3xl leading-3xl hover:opacity-50 transition duration-300 ease-in-out'>
              <a href={`mailto:${email}`}>{email}</a>
            </p>
          </div>

          <div className='mt-10'>
            <h6 className='font-semibold text-base leading-base'>Connect</h6>
            <div className='flex flex-row items-center gap-x-4 mt-3'>
              {socials.map((site, i) => (
                <a key={i} href={site.url} target='_blank' rel='noreferrer'>
                  <Image {...site.icon} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className='col-start-2 col-end-14 pt-8 mt-44 border-t border-white grid grid-cols-12'>
          <div className='col-start-1 col-end-6 flex flex-row gap-x-6'>
            {footerImages.map((img, i) => {
              return (
                <div
                  key={i}
                  className='w-full flex items-center justify-center'>
                  <a href={img.url} target='_blank' rel='noreferrer'>
                    <Image {...img.icon} />
                  </a>
                </div>
              );
            })}
          </div>

          <div className='col-start-11 col-end-13 flex flex-col text-right text-sm leading-sm items-end'>
            <button
              className='flex items-center gap-x-4'
              onClick={() => window && window?.scrollTo(0, 0)}>
              <span className='font-semibold'>Back to top</span>
              <svg
                width='8'
                height='14'
                viewBox='0 0 8 14'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'>
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M-3.31009e-07 6.42741L3.13812 6.42741L3.13812 14L4.46409 14L4.46409 6.42741L8 6.42741L3.8895 -1.12369e-06L-3.31009e-07 6.42741Z'
                  fill='white'
                />
              </svg>
            </button>
            <Link
              to='/privacy-policy'
              className='hover:opacity-50 transition duration-300 ease-in-out mt-2'>
              Privacy
            </Link>
            {/* <Link to="/terms-and-conditions" className="hover:opacity-50 transition duration-300 ease-in-out">Terms & Conditions</Link> */}
          </div>
        </div>
      </div>

      {/* Mobile Footer */}
      <div className='lg:hidden bg-black pt-12 pb-32 grid grid-cols-14 text-white text-center'>
        {/* Top row */}
        <div className='col-start-2 col-end-14 text-base leading-base'>
          <div>
            <h6 className='font-semibold text-sm leading-sm hover:opacity-50 transition duration-300 ease-in-out'>
              <Link to='/contact'>Contact us</Link>
            </h6>
            <p className='mt-4 text-xl leading-xl'>{phone}</p>
            <p className='text-xl leading-xl'>{email}</p>
          </div>

          <h6 className='font-semibold text-sm leading-sm mt-10'>Address</h6>
          <div className='mt-3'>
            {address1.map((line, i) => (
              <p key={i} className='text-sm leading-sm'>
                {line}
              </p>
            ))}
          </div>
          <div className='mt-3'>
            {address2.map((line, i) => (
              <p key={i} className='text-sm leading-sm'>
                {line}
              </p>
            ))}
          </div>
        </div>

        <div className='col-start-2 col-end-14 lg:col-start-11 lg:col-end-14'>
          <div className='mt-10'>
            <h6 className='text-sm leading-sm font-semibold'>Connect</h6>
            <div className='flex flex-row justify-center items-center gap-x-4 mt-3'>
              {socials.map((site, i) => (
                <a key={i} href={site.url} target='_blank' rel='noreferrer'>
                  <Image {...site.icon} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className='col-start-2 col-end-14 mt-16'>
          <Link to='/'>
            <StaticImage
              width={268}
              height={97}
              src={'../../images/logo-footer.png'}
              alt='DSO Logo'
            />
          </Link>

          <div className='mt-6 text-xs leading-xs w-72 mx-auto'>
            {footerCopy.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>

        {/* Bottom Row */}
        <div className='col-start-2 col-end-14 pt-12 mt-16 border-t border-white border-opacity-50 grid grid-cols-12'>
          <div className='col-start-1 col-end-13 grid grid-cols-2 gap-x-8 px-4 gap-y-6'>
            {footerImages.map((img, i) => {
              return (
                <div
                  className='w-full flex items-center justify-center col-span-1'
                  key={i}>
                  <a href={img.url} target='_blank' rel='noreferrer'>
                    <Image className='max-h-12 w-auto' {...img.icon} />
                  </a>
                </div>
              );
            })}
          </div>

          <div className='col-start-1 col-end-13 flex flex-col text-xs leading-xs text-center items-center mt-10'>
            <Link to='/privacy-policy'>Privacy</Link>
            {/* <Link to="/terms-and-conditions" className="mt-2">Terms & Conditions</Link> */}
            <button
              className='flex items-center gap-x-4 mt-4'
              onClick={() => window && window?.scrollTo(0, 0)}>
              <span className='font-semibold'>Back to top</span>
              <svg
                width='8'
                height='14'
                viewBox='0 0 8 14'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'>
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M-3.31009e-07 6.42741L3.13812 6.42741L3.13812 14L4.46409 14L4.46409 6.42741L8 6.42741L3.8895 -1.12369e-06L-3.31009e-07 6.42741Z'
                  fill='white'
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
